import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", email: "", whatsapp_number: "", whatsapp_opt_in: true, role_label: "Manager" };

export default function Managers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/managers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { name, email, whatsapp_number, whatsapp_opt_in, role_label } = form;
      const body = { name, email, whatsapp_number: whatsapp_number || null, whatsapp_opt_in, role_label };
      if (editing) {
        await api.patch(`/managers/${editing}`, body);
        toast.success("Manager updated");
      } else {
        const { data } = await api.post("/managers", body);
        if (data.initial_password) {
          toast.success("Manager added — login provisioned", {
            description: `Email: ${data.login_email} · Password: ${data.initial_password}`,
            duration: 10000,
          });
        } else {
          toast.success("Manager added");
        }
      }
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this manager?")) return;
    await api.delete(`/managers/${id}`); toast.success("Deleted"); load();
  };

  const toggleActive = async (m) => {
    await api.patch(`/managers/${m.id}`, { active: !m.active }); load();
  };

  const startEdit = (m) => {
    setEditing(m.id); setForm({ ...empty, ...m }); setOpen(true);
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Team Directory</div>
          <h1 className="font-display text-3xl font-bold">Managers</h1>
          <div className="text-sm text-zinc-400 mt-1">{rows.length} tracked · configure email & WhatsApp routing</div>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-manager-button" className="bg-indigo-500 hover:bg-indigo-600 text-white">
                <Plus className="w-4 h-4 mr-2" /> Add Manager
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121215] border-[#27272a] text-zinc-100">
              <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Manager" : "New Manager"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {[
                  ["name", "Full name"], ["email", "Email"],
                  ["role_label", "Department / Role"],
                  ["whatsapp_number", "WhatsApp (+91…)"],
                ].map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label>
                    <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      data-testid={`manager-form-${k}`}
                      className="mt-1 bg-[#0f0f12] border-[#27272a]" />
                  </div>
                ))}
                <div className="flex items-center justify-between p-3 rounded-lg bg-[#0f0f12] border border-[#27272a]">
                  <div>
                    <div className="text-sm">Daily WhatsApp reminder</div>
                    <div className="text-xs text-zinc-500">Remind this manager if today's EOD isn't submitted.</div>
                  </div>
                  <Switch checked={form.whatsapp_opt_in !== false}
                    data-testid="manager-form-whatsapp_opt_in"
                    onCheckedChange={(v) => setForm({ ...form, whatsapp_opt_in: v })} />
                </div>
                {!editing && (
                  <div className="text-xs text-zinc-500 p-3 rounded-lg bg-[#0f0f12] border border-[#27272a]">
                    A login will be auto-created with the default password{" "}
                    <span className="font-mono text-zinc-200">eod123</span>. Share it privately with the manager after creation.
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={save} data-testid="manager-save-button"
                  className="bg-indigo-500 hover:bg-indigo-600 text-white">
                  {editing ? "Save changes" : "Add manager"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="bg-[#121215] border-[#27272a] divide-y divide-[#1f1f23]">
        {rows.length === 0 && <div className="p-8 text-center text-zinc-500">No managers yet.</div>}
        {rows.map((m) => (
          <div key={m.id} className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 flex items-center justify-center text-xs font-medium">
              {m.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-medium">{m.name}</div>
                <Badge variant="outline" className="border-[#3f3f46] text-zinc-400 text-[10px]">{m.role_label}</Badge>
                {!m.active && <Badge className="bg-zinc-800 text-zinc-400">Inactive</Badge>}
                {m.active && !m.whatsapp_number && (
                  <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[10px]">No WhatsApp</Badge>
                )}
                {m.whatsapp_number && m.whatsapp_opt_in === false && (
                  <Badge variant="outline" className="border-zinc-600 text-zinc-400 text-[10px]">Reminders off</Badge>
                )}
              </div>
              <div className="text-xs text-zinc-500 flex items-center gap-3 mt-0.5">
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{m.email}</span>
                {m.whatsapp_number && <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{m.whatsapp_number}</span>}
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 pr-2">
                  <Switch checked={m.active} onCheckedChange={() => toggleActive(m)}
                    data-testid={`manager-active-toggle-${m.id}`} />
                </div>
                <Button variant="ghost" size="icon" onClick={() => startEdit(m)}
                  data-testid={`manager-edit-${m.id}`}
                  className="text-zinc-400 hover:text-zinc-100">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(m.id)}
                  data-testid={`manager-delete-${m.id}`}
                  className="text-zinc-400 hover:text-rose-400">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </Card>
    </div>
  );
}
